import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { useRef } from "react";
import { Bold, Italic, List, ListOrdered, Heading2, Heading3, Quote, Link2, Link2Off, Image as ImageIcon, Undo, Redo } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const Btn = ({ active, onClick, title, children, disabled }) => (
    <button type="button" onClick={onClick} title={title} disabled={disabled} className={`p-2 hover:bg-brand-sage/20 transition border-r border-brand-border last:border-r-0 ${active ? "bg-brand-sage/30 text-brand-ink" : "text-brand-sageDark"} disabled:opacity-40 disabled:cursor-not-allowed`}>{children}</button>
);

export default function RichTextEditor({ value = "", onChange, placeholder = "Rédigez votre texte…", testId = "rich-editor" }) {
    const fileRef = useRef(null);
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [2, 3] } }),
            Link.configure({ openOnClick: false, HTMLAttributes: { class: "underline text-brand-sage" } }),
            Image.configure({ HTMLAttributes: { class: "my-4 max-w-full" } }),
            Placeholder.configure({ placeholder }),
        ],
        content: value || "",
        onUpdate: ({ editor }) => onChange(editor.getHTML()),
        editorProps: { attributes: { class: "min-h-[260px] px-4 py-3 prose prose-sm max-w-none focus:outline-none text-brand-ink" } },
    });
    if (!editor) return null;
    const setLink = () => {
        const previousUrl = editor.getAttributes("link").href;
        const url = window.prompt("URL du lien :", previousUrl || "https://");
        if (url === null) return;
        if (url === "") return editor.chain().focus().extendMarkRange("link").unsetLink().run();
        editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    };
    const uploadImg = async (file) => {
        if (!file?.type?.startsWith("image/")) return;
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
            editor.chain().focus().setImage({ src: data.url }).run();
            toast.success("Image insérée");
        } catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <div className="border border-brand-border bg-brand-bg" data-testid={testId}>
            <div className="flex flex-wrap items-center border-b border-brand-border bg-brand-bgalt">
                <Btn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Gras"><Bold size={14} /></Btn>
                <Btn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italique"><Italic size={14} /></Btn>
                <Btn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Titre 2"><Heading2 size={14} /></Btn>
                <Btn active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Titre 3"><Heading3 size={14} /></Btn>
                <Btn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Liste à puces"><List size={14} /></Btn>
                <Btn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Liste numérotée"><ListOrdered size={14} /></Btn>
                <Btn active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Citation"><Quote size={14} /></Btn>
                <Btn active={editor.isActive("link")} onClick={setLink} title="Lien"><Link2 size={14} /></Btn>
                <Btn onClick={() => editor.chain().focus().unsetLink().run()} disabled={!editor.isActive("link")} title="Retirer le lien"><Link2Off size={14} /></Btn>
                <Btn onClick={() => fileRef.current?.click()} title="Insérer une image"><ImageIcon size={14} /></Btn>
                <div className="flex-1" />
                <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Annuler"><Undo size={14} /></Btn>
                <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Rétablir"><Redo size={14} /></Btn>
            </div>
            <EditorContent editor={editor} />
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg(e.target.files?.[0])} />
        </div>
    );
}
